/**
 * QA Testing Service
 * Handles API communication for test cases, bug reports, and QA statistics
 */

import api from './api';

export interface TestStep {
  id: number;
  description: string;
  expected: string;
  actual?: string;
  status: 'passed' | 'failed' | 'pending';
}

export interface TestCase {
  id: number;
  project_id: number;
  created_by: number;
  assigned_to?: number;
  title: string;
  description: string;
  type: 'unit' | 'integration' | 'e2e' | 'performance' | 'security' | 'manual';
  status: 'passed' | 'failed' | 'pending' | 'running' | 'skipped';
  priority: 'high' | 'medium' | 'low';
  environment: string;
  last_run_at?: string;
  duration_seconds?: number;
  run_count: number;
  pass_count: number;
  fail_count: number;
  steps?: TestStep[];
  tags?: string[];
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
  // Computed attributes
  assignee_name?: string;
  creator_name: string;
  success_rate: number;
  // Relationships
  project?: { id: number; name: string };
  assignee?: { id: number; name: string };
  creator?: { id: number; name: string };
}

export interface BugReport {
  id: number;
  project_id: number;
  reported_by: number;
  assigned_to?: number;
  test_case_id?: number;
  title: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  status: 'open' | 'in-progress' | 'resolved' | 'closed' | 'wont-fix';
  found_in_version?: string;
  fixed_in_version?: string;
  steps_to_reproduce?: string[];
  expected_behavior?: string;
  actual_behavior?: string;
  attachments?: string[];
  resolution_notes?: string;
  resolved_at?: string;
  resolved_by?: number;
  browser?: string;
  os?: string;
  device?: string;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
  // Computed attributes
  reporter_name: string;
  assignee_name?: string;
  resolver_name?: string;
  is_resolved: boolean;
  age_in_days: number;
  // Relationships
  project?: { id: number; name: string };
  reporter?: { id: number; name: string };
  assignee?: { id: number; name: string };
  resolver?: { id: number; name: string };
  test_case?: TestCase;
}

export interface TestRun {
  id: number;
  test_case_id: number;
  executed_by: number;
  status: 'passed' | 'failed' | 'skipped';
  duration_seconds: number;
  environment: string;
  step_results?: any[];
  error_message?: string;
  stack_trace?: string;
  screenshots?: string[];
  metadata?: Record<string, any>;
  executed_at: string;
  created_at: string;
  updated_at: string;
  executor_name: string;
}

export interface QAStatistics {
  test_cases: {
    total: number;
    passed: number;
    failed: number;
    pending: number;
    running: number;
    skipped: number;
  };
  test_coverage: number;
  bug_reports: {
    total: number;
    open: number;
    in_progress: number;
    resolved: number;
    closed: number;
    critical: number;
    high: number;
  };
  success_rate: number;
}

class QATestingService {
  /**
   * Get all test cases with optional filters
   */
  async getTestCases(filters?: {
    project_id?: number;
    type?: string;
    status?: string;
    priority?: string;
    assigned_to?: number;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }): Promise<TestCase[]> {
    const response = await api.get<{ success: boolean; data: TestCase[] }>(
      '/project-manager/qa/test-cases',
      { params: filters }
    );
    return response.data.data;
  }

  /**
   * Create a new test case
   */
  async createTestCase(testCase: {
    project_id: number;
    title: string;
    description: string;
    type: TestCase['type'];
    priority: TestCase['priority'];
    assigned_to?: number;
    environment?: string;
    steps?: TestStep[];
    tags?: string[];
  }): Promise<TestCase> {
    const response = await api.post<{ success: boolean; data: TestCase; message: string }>(
      '/project-manager/qa/test-cases',
      testCase
    );
    return response.data.data;
  }

  /**
   * Update a test case
   */
  async updateTestCase(
    id: number,
    updates: Partial<{
      title: string;
      description: string;
      type: TestCase['type'];
      priority: TestCase['priority'];
      status: TestCase['status'];
      assigned_to: number | null;
      environment: string;
      steps: TestStep[];
      tags: string[];
    }>
  ): Promise<TestCase> {
    const response = await api.put<{ success: boolean; data: TestCase; message: string }>(
      `/project-manager/qa/test-cases/${id}`,
      updates
    );
    return response.data.data;
  }

  /**
   * Delete a test case
   */
  async deleteTestCase(id: number): Promise<void> {
    await api.delete(`/project-manager/qa/test-cases/${id}`);
  }

  /**
   * Execute a test case
   */
  async executeTest(
    id: number,
    execution: {
      status: 'passed' | 'failed' | 'skipped';
      duration_seconds: number;
      environment?: string;
      step_results?: any[];
      error_message?: string;
      stack_trace?: string;
    }
  ): Promise<{ test_case: TestCase; test_run: TestRun }> {
    const response = await api.post<{
      success: boolean;
      data: { test_case: TestCase; test_run: TestRun };
      message: string;
    }>(`/project-manager/qa/test-cases/${id}/execute`, execution);
    return response.data.data;
  }

  /**
   * Get all bug reports with optional filters
   */
  async getBugReports(filters?: {
    project_id?: number;
    severity?: string;
    status?: string;
    assigned_to?: number;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }): Promise<BugReport[]> {
    const response = await api.get<{ success: boolean; data: BugReport[] }>(
      '/project-manager/qa/bug-reports',
      { params: filters }
    );
    return response.data.data;
  }

  /**
   * Create a new bug report
   */
  async createBugReport(bugReport: {
    project_id: number;
    title: string;
    description: string;
    severity: BugReport['severity'];
    assigned_to?: number;
    test_case_id?: number;
    found_in_version?: string;
    steps_to_reproduce?: string[];
    expected_behavior?: string;
    actual_behavior?: string;
    attachments?: string[];
    browser?: string;
    os?: string;
    device?: string;
  }): Promise<BugReport> {
    const response = await api.post<{ success: boolean; data: BugReport; message: string }>(
      '/project-manager/qa/bug-reports',
      bugReport
    );
    return response.data.data;
  }

  /**
   * Update a bug report
   */
  async updateBugReport(
    id: number,
    updates: Partial<{
      title: string;
      description: string;
      severity: BugReport['severity'];
      status: BugReport['status'];
      assigned_to: number | null;
      fixed_in_version: string;
      resolution_notes: string;
    }>
  ): Promise<BugReport> {
    const response = await api.put<{ success: boolean; data: BugReport; message: string }>(
      `/project-manager/qa/bug-reports/${id}`,
      updates
    );
    return response.data.data;
  }

  /**
   * Delete a bug report
   */
  async deleteBugReport(id: number): Promise<void> {
    await api.delete(`/project-manager/qa/bug-reports/${id}`);
  }

  /**
   * Get QA statistics
   */
  async getStatistics(projectId?: number): Promise<QAStatistics> {
    const response = await api.get<{ success: boolean; data: QAStatistics }>(
      '/project-manager/qa/statistics',
      { params: projectId ? { project_id: projectId } : undefined }
    );
    return response.data.data;
  }
}

export default new QATestingService();
